import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Banknote, AlertTriangle, Building2, Users, Globe, Mail, Phone, MapPin, ExternalLink, ShieldAlert } from 'lucide-react';
import type { DossierSectionProps } from './types';
import type { CompanyRecord } from '@/types/profiler';
import { BTRAGraph } from './BTRAGraph';
import { AttackSurface } from './AttackSurface';

// ── mini metric card ──────────────────────────────────────────────────────────

function WealthCard({ label, value, sub, color = '#00C8FF' }: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 14px' }}
    >
      <div style={{ color, fontFamily: 'monospace', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: 4 }}>
        {label}
      </div>
      {sub && (
        <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

// ── status colour ──────────────────────────────────────────────────────────────

function statusColor(s?: string) {
  const u = (s ?? '').toUpperCase();
  if (u === 'ACTIVE') return '#00C853';
  if (u.includes('STRUCK') || u.includes('DISSOLV')) return '#FF1F1F';
  if (u === 'DORMANT') return '#F0B429';
  return '#5A7A9A';
}

// ── company profile card ───────────────────────────────────────────────────────

function CompanyCard({ rec, highRisk }: { rec: CompanyRecord; highRisk: boolean }) {
  const sc = statusColor(rec.status);
  return (
    <div
      style={{
        background: '#0C1118',
        border: `1px solid ${highRisk ? '#FF1F1F30' : '#182030'}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          background: '#090D13',
          borderBottom: '1px solid #182030',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: `${sc}18`,
              border: `1px solid ${sc}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={16} style={{ color: sc }} />
          </div>
          <div>
            <p style={{ color: '#DCE8F5', fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{rec.name}</p>
            {rec.cin && (
              <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 2 }}>
                CIN: {rec.cin}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span
            style={{
              background: `${sc}18`,
              color: sc,
              border: `1px solid ${sc}40`,
              fontSize: 9, fontFamily: 'monospace', fontWeight: 700,
              padding: '3px 10px', borderRadius: 99,
            }}
          >
            {rec.status || 'UNKNOWN'}
          </span>
          {highRisk && (
            <span
              style={{
                background: '#FF1F1F18', color: '#FF1F1F',
                border: '1px solid #FF1F1F40',
                fontSize: 9, fontFamily: 'monospace', fontWeight: 700,
                padding: '3px 10px', borderRadius: 99,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <ShieldAlert size={9} /> HIGH RISK
            </span>
          )}
        </div>
      </div>

      {/* Details grid */}
      <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {rec.address && (
          <DetailRow icon={<MapPin size={11} style={{ color: '#F0B429' }} />} label="REGISTERED ADDRESS" value={rec.address} wide />
        )}
        {rec.email && (
          <DetailRow icon={<Mail size={11} style={{ color: '#00C8FF' }} />} label="EMAIL" value={rec.email} />
        )}
        {rec.phone && (
          <DetailRow icon={<Phone size={11} style={{ color: '#00C853' }} />} label="PHONE" value={rec.phone} />
        )}
        {rec.website && (
          <DetailRow
            icon={<Globe size={11} style={{ color: '#2979FF' }} />}
            label="WEBSITE"
            value={rec.website}
            link
          />
        )}
        {rec.nature_of_business && (
          <DetailRow icon={<Building2 size={11} style={{ color: '#F0B429' }} />} label="NATURE OF BUSINESS" value={rec.nature_of_business} />
        )}
        {rec.registration_date && (
          <DetailRow icon={<Banknote size={11} style={{ color: '#5A7A9A' }} />} label="INCORPORATION DATE" value={String(rec.registration_date)} />
        )}
        {rec.authorized_capital && (
          <DetailRow icon={<Banknote size={11} style={{ color: '#F0B429' }} />} label="AUTHORISED CAPITAL" value={rec.authorized_capital} />
        )}
        {rec.paid_up_capital && (
          <DetailRow icon={<Banknote size={11} style={{ color: '#00C853' }} />} label="PAID-UP CAPITAL" value={rec.paid_up_capital} />
        )}
      </div>

      {/* Directors */}
      {rec.co_directors && rec.co_directors.length > 0 && (
        <div style={{ borderTop: '1px solid #182030', padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Users size={11} style={{ color: '#5A7A9A' }} />
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.12em' }}>
              DIRECTORS / PARTNERS ({rec.co_directors.length})
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {rec.co_directors.map((name, i) => (
              <span
                key={i}
                style={{
                  background: '#0F1928',
                  border: '1px solid #1E2B3F',
                  color: '#DCE8F5',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  padding: '4px 10px',
                  borderRadius: 6,
                }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({
  icon, label, value, wide, link,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  wide?: boolean;
  link?: boolean;
}) {
  return (
    <div style={{ gridColumn: wide ? 'span 2' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
        {icon}
        <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
          {label}
        </span>
      </div>
      {link ? (
        <a
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#2979FF', fontSize: 10, fontFamily: 'monospace', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          {value} <ExternalLink size={9} />
        </a>
      ) : (
        <span style={{ color: '#DCE8F5', fontSize: 10, lineHeight: 1.5, wordBreak: 'break-word' }}>
          {value}
        </span>
      )}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function Financial({ pipeline, report }: DossierSectionProps) {
  const wealth = pipeline?.wealth;
  const corporate = pipeline?.corporate;
  const ato = pipeline?.ato;
  const exposure = pipeline?.company_exposure;

  const upiCount = wealth?.upi_handles?.length ?? 0;
  const propertyValue = wealth?.total_property_value_approx_lakhs ?? 0;
  const vehicleCount = wealth?.vehicle_assets?.vehicle_count ?? 0;
  const creditScore = wealth?.credit?.score;
  const cryptoReg = wealth?.crypto_registered;

  const mca21 = corporate?.mca21;
  const companyRecords = mca21?.companies_as_director ?? [];
  const sebi = corporate?.sebi;

  const isCompany = report?.seed_type === 'COMPANY';
  const sanctionHits = pipeline?.sanctions?.hits ?? [];
  const breachCount = ato?.breach_count ?? 0;
  const breachSources = ato?.breach_sources ?? [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Corporate profile */}
      {companyRecords.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Building2 size={12} style={{ color: '#F0B429' }} />
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
              INDIA CORPORATE REGISTRY — MCA21 / InstaFinancials
            </span>
            {mca21?.high_risk_flag && (
              <span
                style={{
                  background: '#FF1F1F18', color: '#FF1F1F', border: '1px solid #FF1F1F30',
                  fontSize: 8, fontFamily: 'monospace', fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                }}
              >
                {mca21.struck_off_connections} STRUCK OFF
              </span>
            )}
          </div>
          <div className="flex flex-col gap-3">
            {companyRecords.map((rec, i) => (
              <CompanyCard key={i} rec={rec} highRisk={mca21?.high_risk_flag ?? false} />
            ))}
          </div>
        </div>
      )}

      {/* SEBI status */}
      {sebi && (sebi.is_registered || sebi.is_debarred) && (
        <div
          style={{
            background: '#0C1118',
            border: `1px solid ${sebi.is_debarred ? '#FF1F1F30' : '#182030'}`,
            borderRadius: 8,
            padding: '12px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 4 }}>
                SEBI REGISTRATION
              </p>
              <p style={{ color: '#DCE8F5', fontSize: 11 }}>
                {sebi.category ?? 'Registered Intermediary'}
                {sebi.registration_no && (
                  <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginLeft: 8 }}>
                    {sebi.registration_no}
                  </span>
                )}
              </p>
            </div>
            {sebi.is_debarred ? (
              <span style={{ background: '#FF1F1F18', color: '#FF1F1F', border: '1px solid #FF1F1F30', fontSize: 9, fontFamily: 'monospace', fontWeight: 700, padding: '3px 10px', borderRadius: 99 }}>
                DEBARRED
              </span>
            ) : (
              <span style={{ background: '#00C85318', color: '#00C853', border: '1px solid #00C85330', fontSize: 9, fontFamily: 'monospace', fontWeight: 700, padding: '3px 10px', borderRadius: 99 }}>
                REGISTERED
              </span>
            )}
          </div>
        </div>
      )}

      {/* Wealth metrics */}
      <div>
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
          WEALTH SIGNALS
        </p>
        <div className="grid grid-cols-4 gap-3">
          <WealthCard
            label="UPI HANDLES"
            value={upiCount}
            color={upiCount > 0 ? '#00C8FF' : '#5A7A9A'}
            sub="Verified VPA"
          />
          <WealthCard
            label="PROPERTY VALUE"
            value={propertyValue > 0 ? `₹${propertyValue.toFixed(1)}L` : '—'}
            color={propertyValue > 0 ? '#F0B429' : '#5A7A9A'}
            sub="Approx. (lakhs)"
          />
          <WealthCard
            label="VEHICLES"
            value={vehicleCount}
            color={vehicleCount > 0 ? '#F0B429' : '#5A7A9A'}
            sub={wealth?.vehicle_assets?.total_vehicle_value_approx_lakhs
              ? `₹${wealth.vehicle_assets.total_vehicle_value_approx_lakhs.toFixed(1)}L est.`
              : 'RC lookup'}
          />
          <WealthCard
            label="CREDIT SCORE"
            value={creditScore ?? '—'}
            color={
              creditScore === undefined ? '#5A7A9A'
                : creditScore >= 750 ? '#00C853'
                : creditScore >= 650 ? '#F0B429'
                : '#FF1F1F'
            }
            sub={wealth?.credit?.bureau ?? 'Credit bureau'}
          />
        </div>
      </div>

      {/* UPI handles detail */}
      {(wealth?.upi_handles?.length ?? 0) > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            UPI HANDLES
          </p>
          <div className="flex flex-wrap gap-2">
            {wealth!.upi_handles!.map((vpa, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{
                  background: '#0C1118',
                  border: `1px solid ${vpa.verified ? '#00C8FF40' : '#182030'}`,
                }}
              >
                <span style={{ color: '#00C8FF', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
                  {vpa.handle}
                </span>
                {vpa.verified && (
                  <span
                    style={{
                      background: '#00C85318',
                      color: '#00C853',
                      border: '1px solid #00C85330',
                      fontSize: 8,
                      fontFamily: 'monospace',
                      padding: '1px 5px',
                      borderRadius: 99,
                    }}
                  >
                    ACTIVE
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crypto section */}
      {cryptoReg !== undefined && (
        <div
          style={{
            background: '#0C1118',
            border: `1px solid ${cryptoReg ? '#FF6B1A40' : '#182030'}`,
            borderRadius: 8,
            padding: '12px 14px',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 2 }}>
                CRYPTOCURRENCY / EXCHANGE
              </p>
              <p style={{ color: cryptoReg ? '#FF6B1A' : '#5A7A9A', fontSize: 11, fontWeight: 600 }}>
                {cryptoReg
                  ? 'Exchange account registration detected (WazirX / CoinDCX)'
                  : 'No exchange registration detected'}
              </p>
            </div>
            <span
              style={{
                background: cryptoReg ? '#FF6B1A18' : '#5A7A9A18',
                color: cryptoReg ? '#FF6B1A' : '#5A7A9A',
                border: `1px solid ${cryptoReg ? '#FF6B1A30' : '#5A7A9A30'}`,
                fontSize: 9,
                fontFamily: 'monospace',
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 99,
              }}
            >
              {cryptoReg ? 'REGISTERED' : 'NONE FOUND'}
            </span>
          </div>
        </div>
      )}

      {/* Breach exposure */}
      {ato && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            BREACH EXPOSURE SUMMARY
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div
              style={{ background: '#0C1118', border: `1px solid ${(ato.breach_count ?? 0) > 0 ? '#FF1F1F30' : '#182030'}`, borderRadius: 8, padding: '12px 14px' }}
            >
              <div style={{ color: (ato.breach_count ?? 0) > 0 ? '#FF1F1F' : '#5A7A9A', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
                {ato.breach_count ?? 0}
              </div>
              <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>BREACH SOURCES</div>
              {ato.most_recent_breach && (
                <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>
                  Latest: {ato.most_recent_breach}
                </div>
              )}
            </div>
            <div
              style={{ background: '#0C1118', border: `1px solid ${(ato.stealer_log_count ?? 0) > 0 ? '#FF6B1A30' : '#182030'}`, borderRadius: 8, padding: '12px 14px' }}
            >
              <div style={{ color: (ato.stealer_log_count ?? 0) > 0 ? '#FF6B1A' : '#5A7A9A', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
                {ato.stealer_log_count ?? 0}
              </div>
              <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>STEALER LOGS</div>
              <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>Dark-web infostealer</div>
            </div>
            <div
              style={{ background: '#0C1118', border: `1px solid ${(ato.email_exposure_count ?? 0) > 0 ? '#F0B42930' : '#182030'}`, borderRadius: 8, padding: '12px 14px' }}
            >
              <div style={{ color: (ato.email_exposure_count ?? 0) > 0 ? '#F0B429' : '#5A7A9A', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
                {ato.email_exposure_count ?? 0}
              </div>
              <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>EMAIL EXPOSURES</div>
              <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>Credential databases</div>
            </div>
          </div>
        </div>
      )}

      {/* degraded sources */}
      {(wealth?.degraded_sources?.length ?? 0) > 0 && (
        <div
          style={{ background: '#1A1000', border: '1px solid #F0B42920', borderRadius: 8, padding: '8px 12px' }}
          className="flex items-center gap-2"
        >
          <AlertTriangle size={12} style={{ color: '#F0B429', flexShrink: 0 }} />
          <span style={{ color: '#F0B429', fontSize: 9, fontFamily: 'monospace' }}>
            Degraded sources: {wealth!.degraded_sources!.join(', ')}
          </span>
        </div>
      )}

      {/* BTRA Graph — company exposure topology */}
      {isCompany && companyRecords.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            BTRA — BUSINESS THREAT &amp; RISK ASSESSMENT GRAPH
          </p>
          <BTRAGraph
            companyName={report?.seed ?? pipeline?.cluster?.canonical ?? 'Company'}
            riskTier={report?.risk_tier ?? 'MEDIUM'}
            companies={companyRecords}
            sanctionHits={sanctionHits}
            exposure={exposure}
            breachCount={breachCount}
            breachSources={breachSources}
          />
        </div>
      )}

      {/* Attack surface panel */}
      {exposure && (
        <AttackSurface exposure={exposure} />
      )}

      {/* Empty state */}
      {companyRecords.length === 0 && !wealth && !ato && !exposure && (
        <div
          style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '48px', textAlign: 'center' }}
        >
          <Banknote size={28} style={{ color: '#283A50', margin: '0 auto 10px' }} />
          <span style={{ color: '#283A50', fontSize: 11, fontFamily: 'monospace' }}>
            No financial data available
          </span>
        </div>
      )}
    </motion.div>
  );
}
